import { type DocumentInfo, ErrorResponse, type ErrorType } from 'api';
import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

interface DocumentContextValue {
  document: DocumentInfo;
  isLoading: boolean;
  error: ErrorType<ErrorResponse> | null;
}

const DocumentContext = createContext<DocumentContextValue | null>(null);

interface DocumentProviderProps {
  document: DocumentInfo;
  isLoading: boolean;
  error: ErrorType<ErrorResponse> | null;
}

export const DocumentProvider = ({
  children,
  document,
  isLoading,
  error,
}: PropsWithChildren<DocumentProviderProps>) => {
  const value = useMemo(() => {
    return {
      document,
      error,
      isLoading,
    };
  }, [document, isLoading, error]);

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
};

export const useDocument = (): DocumentContextValue => {
  const context = useContext(DocumentContext);
  if (!context) {
    throw new Error('useDocument must be used within a DocumentProvider');
  }
  return context;
};
